<template>
  <div
    v-if="blok.image || blok.title || blok.text"
    v-editable="blok"
    class="storyblok-image-background-card"
    :class="[
      `storyblok-image-background-card--${variant}`,
      {
        'storyblok-image-background-card--clickable': isCardClickable,
        'storyblok-image-background-card--default-text': !useContrastText,
      },
    ]"
    :style="backgroundStyle"
    :role="isCardClickable ? 'link' : undefined"
    :tabindex="isCardClickable ? 0 : undefined"
    @click="openCardLink"
    @keydown.enter.prevent="openCardLink"
    @keydown.space.prevent="openCardLink"
  >
    <div class="storyblok-image-background-card__overlay" />
    <div class="storyblok-image-background-card__content">
      <h3 v-if="blok.title" class="storyblok-image-background-card__title">
        {{ blok.title }}
      </h3>
      <div v-if="richTextBlok" class="storyblok-image-background-card__text">
        <StoryblokRichText :blok="richTextBlok" />
      </div>
      <p v-else-if="plainText" class="storyblok-image-background-card__text">
        {{ plainText }}
      </p>
      <div v-if="buttonBlok" class="storyblok-image-background-card__button">
        <StoryblokButton :blok="buttonBlok" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import StoryblokButton from '@/features/storyblok/components/StoryblokButton.vue';
import StoryblokRichText from '@/features/storyblok/components/StoryblokRichText.vue';
import { CommonRoutesName } from '@/router/constants/routesName';
import {
  LinkType,
  type StoryblokButtonBlok,
  type StoryblokCardBlok,
  type StoryblokRichTextBlok,
} from '@/features/storyblok/types';
import {
  extractStorySlugFromWardPath,
  resolveStoryblokLink,
} from '@/features/storyblok/utils/link';

const props = withDefaults(
  defineProps<{
    blok: StoryblokCardBlok;
    variant?: 'hero' | 'compact';
  }>(),
  {
    variant: 'compact',
  }
);

const router = useRouter();
const useContrastText = computed(() => props.blok.textColorContrast !== false);

const backgroundStyle = computed(() => {
  if (!props.blok.image?.filename) {
    return {};
  }

  return {
    backgroundImage: `url('${props.blok.image.filename}')`,
  };
});

const richTextBlok = computed<StoryblokRichTextBlok | null>(() =>
  typeof props.blok.text === 'string' || !props.blok.text
    ? null
    : {
        content: props.blok.text,
      }
);

const plainText = computed(() =>
  typeof props.blok.text === 'string' ? props.blok.text.trim() : undefined
);

const buttonBlok = computed<StoryblokButtonBlok | null>(() => {
  if (!props.blok.link || !props.blok.buttonTitle) {
    return null;
  }

  return {
    title: props.blok.buttonTitle,
    linkType: props.blok.linkType ?? LinkType.WARD,
    buttonLink: props.blok.link,
    position: props.blok.buttonPosition ?? 'left',
    style: props.blok.buttonStyle ?? 'standard',
  };
});

const cardTarget = computed(() => {
  if (buttonBlok.value || !props.blok.link) {
    return null;
  }

  return resolveStoryblokLink(props.blok.link, props.blok.linkType ?? LinkType.WARD);
});

const isCardClickable = computed(() => !!cardTarget.value);

const openCardLink = (event: MouseEvent | KeyboardEvent): void => {
  if (!cardTarget.value) {
    return;
  }

  const eventTarget = event.target;
  if (
    eventTarget instanceof HTMLElement &&
    eventTarget.closest('a, button, [role="button"], [role="link"]')
  ) {
    return;
  }

  if (cardTarget.value.type === 'external') {
    window.open(cardTarget.value.href, '_blank', 'noopener,noreferrer');
    return;
  }

  if (cardTarget.value.type === 'storyblok') {
    void router.replace({
      name: CommonRoutesName.StoryblokPage,
      query: { slug: cardTarget.value.slug },
    });
    return;
  }

  const storySlugFromInternalPath = extractStorySlugFromWardPath(cardTarget.value.path);
  if (storySlugFromInternalPath) {
    void router.replace({
      name: CommonRoutesName.StoryblokPage,
      query: { slug: storySlugFromInternalPath },
    });
    return;
  }

  void router.push(cardTarget.value.path);
};
</script>

<style scoped>
.storyblok-image-background-card {
  position: relative;
  display: flex;
  min-height: 320px;
  width: 100%;
  border-radius: var(--ion-radius-3xl, 30px);
  overflow: hidden;
  background-color: var(--ion-color-step-150);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.storyblok-image-background-card--hero {
  min-height: 380px;
}

.storyblok-image-background-card--compact {
  min-height: 360px;
}

.storyblok-image-background-card--clickable {
  cursor: pointer;
}

.storyblok-image-background-card--clickable:focus-visible {
  outline: 2px solid var(--ion-color-primary);
  outline-offset: 4px;
}

.storyblok-image-background-card__overlay {
  position: absolute;
  inset: 0;
}

.storyblok-image-background-card__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--ion-space-4);
  width: min(68%, 520px);
  padding: var(--ion-space-6);
  color: var(--ion-text-color-contrast);
}

.storyblok-image-background-card--default-text .storyblok-image-background-card__content {
  color: var(--ion-text-color-primary);
}

.storyblok-image-background-card__title {
  margin: 0;
  color: inherit;
  font-size: clamp(1.375rem, 2.1vw, 2.375rem);
  line-height: 1.2;
  font-weight: 700;
}

.storyblok-image-background-card__text {
  margin: 0;
  color: inherit;
  font-size: var(--ion-text-base);
  line-height: 1.5;
}

.storyblok-image-background-card__text :deep(.rich-text),
.storyblok-image-background-card__text :deep(p) {
  color: inherit;
  margin: 0;
}

.storyblok-image-background-card__button {
  margin-top: auto;
}

.storyblok-image-background-card__button :deep(.button) {
  background: transparent;
  padding: 0;
}

.storyblok-image-background-card--hero
  .storyblok-image-background-card__button
  :deep(.custom-button) {
  background-color: var(--ion-background-color);
  border-color: transparent;
}

.storyblok-image-background-card--compact .storyblok-image-background-card__content {
  width: 100%;
  justify-content: flex-end;
  gap: var(--ion-space-3);
}

.storyblok-image-background-card--compact .storyblok-image-background-card__title {
  font-size: clamp(1.2rem, 1.35vw, 1.6rem);
  text-transform: uppercase;
}

@media (max-width: 768px) {
  .storyblok-image-background-card--hero {
    min-height: 300px;
  }

  .storyblok-image-background-card__content {
    width: 100%;
    padding: var(--ion-space-4);
  }
}
</style>
